import { Component, OnInit, OnDestroy, input, computed, signal, inject, output } from '@angular/core';
import { BoardCard } from '@app/models/board-card';
import { PlatformService } from '@app/services/platform.service';
import { Router, ActivatedRoute } from '@angular/router';
import { SnackbarService, TypeSnackbar } from '@app/services';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-board',
  templateUrl: './board.component.html',
  styleUrls: ['./board.component.scss'],
  standalone: false
})
export class BoardComponent implements OnInit, OnDestroy {
  headerStyleParts = ['borderColor', 'backgroundColor', 'borderRadius'];

  //Subject
  private onDestroy$ = new Subject<void>();

  //Services
  public platformService = inject(PlatformService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private snackbarService = inject(SnackbarService);
  private translateService = inject(TranslateService);

  // Inputs signals
  boardCards = input<BoardCard[]>();
  compact = input(false);
  previewMode = input(false);
  variables = input<object>({});
  currentCategoryId = input<string>("");

  //Output
  categorySelected = output<string>();

  // Signals et états
  idCategory = signal<string>("");
  isSmallScreen = signal<boolean>(false);

  // Computed values
  currentCards = computed(() => {
    const cards = this.boardCards();
    const categoryId = this.idCategory();
    const routeCategoryId = this.activatedRoute.snapshot.paramMap.get('idCategorie');

    // Mode aperçu
    if (this.previewMode()) {
      if (categoryId) {
        if (!this.currentCategoryId()) {
          return cards;
        }
        return cards.find(card => card.id === categoryId)
          ?.cards.filter(card => card.enabled) || [];
      }
      return cards;
    }

    // Mode normal - Route /part/:idCategorie
    if (routeCategoryId) {
      const categoryIdNumber = routeCategoryId;
      const category = cards.find(card => card.id === categoryIdNumber && card.type === 'category');
      return category?.cards?.filter(card => card.enabled) || [];
    }

    // Route par défaut
    return cards;
  });

  ngOnInit() {
    this.platformService.isSmall$.pipe(takeUntil(this.onDestroy$), distinctUntilChanged()).subscribe(value => this.isSmallScreen.set(value));
  }

  onClickCard(card: BoardCard) {
    if (!this.canAccessCard(card)) return;

    if (this.previewMode()) {
      this.handlePreviewClick(card);
      return;
    }

    this.navigateToCard(card);
  }

  canAccessCard(card: BoardCard): boolean {
    if (!card.disabledCause) return true;

    this.snackbarService.info(
      this.translateService.instant(card.disabledCause)
    );
    return false;
  }

  handlePreviewClick(card: BoardCard): void {
    if (card.type === 'category') {
      this.idCategory.set(card.id);
      this.updateCategorySelected();
      return;
    }

    this.snackbarService.open({
      message: 'Lien inactif pendant la configuration',
      type: TypeSnackbar.info,
      textButton: 'Ok'
    });
  }

  navigateToCard(card: BoardCard): void {
    if (card.component && card.type !== 'category') {
      this.router.navigate([`/account/${card.component}`], {
        state: { cardTitle: card.title }
      });
    }

    if (card.type === 'category') {
      this.router.navigate([`account/part/${card.id}`], {
        state: { cardTitle: card.title }
      });
    }
  }

  updateCategorySelected() {
    this.categorySelected.emit(this.idCategory());
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
